import "./cardInventory.css";
import { useEffect, useRef, useState } from "react";
import { Button } from "antd";
import { OperationArea, Panel } from "../../../components";
import { useApiFetch, useLanguage } from "../../../hooks";
import {
  AdminCardInventoryImportProps,
  AdminCardInventorySummaryProps,
  API_URL,
  apiFetch,
} from "../../../services";
import { formatDateTimeToLocal } from "../../../utils";

// Inventario de tarjetas del cliente (código de activación ↔ código de redención),
// necesario para enlazar las redenciones de tarjetas físicas con su activación. Solo
// se muestran totales: los códigos de redención no salen nunca del back.
const CardInventoryScreen: React.FC = () => {
  const { t } = useLanguage();
  const s = t.adminCardInventoryScreen;

  const [fetchSummary, summary, loading] =
    useApiFetch<AdminCardInventorySummaryProps>({
      total: 0,
      lastImportAt: null,
      pendingRedemptions: 0,
    });
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [result, setResult] = useState<AdminCardInventoryImportProps | null>(
    null
  );
  const [error, setError] = useState<string>("");
  const inputRef = useRef<HTMLInputElement>(null);

  const load = () => fetchSummary(API_URL.ADMIN_CARD_INVENTORY(), { method: "GET" });

  useEffect(() => {
    load();
  }, []);

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    setError("");
    setResult(null);

    const body = new FormData();
    body.append("file", file);
    const res = await apiFetch(API_URL.ADMIN_CARD_INVENTORY(), {
      method: "POST",
      body,
    });
    const data = await res.json().catch(() => null);

    setUploading(false);
    if (!res.ok) {
      setError(data?.error ?? s.uploadError);
      return;
    }

    setResult(data as AdminCardInventoryImportProps);
    setFile(null);
    if (inputRef.current) inputRef.current.value = "";
    await load();
  };

  return (
    <Panel>
      <OperationArea title={s.title}>
        <p className="card-inventory-description">{s.description}</p>

        <section className="card-inventory-stats">
          <div>
            <span>{s.total}</span>
            <strong>{loading ? "…" : summary.total.toLocaleString("es-ES")}</strong>
          </div>
          <div>
            <span>{s.lastImport}</span>
            <strong>
              {summary.lastImportAt
                ? formatDateTimeToLocal(summary.lastImportAt)
                : "—"}
            </strong>
          </div>
          <div>
            <span>{s.pending}</span>
            <strong>{summary.pendingRedemptions.toLocaleString("es-ES")}</strong>
          </div>
        </section>

        <section className="card-inventory-upload">
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          <Button
            type="primary"
            disabled={!file || uploading}
            loading={uploading}
            onClick={handleUpload}
          >
            {s.upload}
          </Button>
        </section>
        <p className="card-inventory-help">{s.help}</p>

        {error && <p className="card-inventory-error">{error}</p>}
        {result && (
          <p className="card-inventory-result">
            {s.result
              .replace("{rows}", String(result.rows))
              .replace("{created}", String(result.created))
              .replace("{updated}", String(result.updated))
              .replace("{skipped}", String(result.skipped))
              .replace("{conflicts}", String(result.conflicts))
              .replace("{relinked}", String(result.relinked))}
          </p>
        )}
      </OperationArea>
    </Panel>
  );
};

export default CardInventoryScreen;
